import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin } from "lucide-react";
import { LOCATIONS, SEGMENTS } from "@/lib/site";

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function ReservationSearchForm({ variant = "hero" }) {
  const navigate = useNavigate();
  const minDate = nowLocal();
  const [form, setForm] = useState({
    pickup: "Taksim",
    differentDropoff: false,
    dropoff: "Taksim",
    pickup_datetime: "",
    return_datetime: "",
    segment: "all",
    pickupOther: "",
    dropoffOther: "",
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (!form.pickup_datetime) err.pickup_datetime = "Alış tarihini seçin";
    if (!form.return_datetime) err.return_datetime = "İade tarihini seçin";
    if (form.pickup_datetime && form.pickup_datetime < minDate) err.pickup_datetime = "Geçmiş tarih seçilemez";
    if (form.pickup_datetime && form.return_datetime && form.return_datetime <= form.pickup_datetime)
      err.return_datetime = "İade zamanı alıştan sonra olmalı";
    if (form.pickup === "Diğer" && !form.pickupOther) err.pickupOther = "Konum girin";
    setErrors(err);
    if (Object.keys(err).length) return;

    const pickupLoc = form.pickup === "Diğer" ? form.pickupOther : form.pickup;
    const dropoffLoc = form.differentDropoff ? (form.dropoff === "Diğer" ? form.dropoffOther : form.dropoff) : pickupLoc;
    const params = new URLSearchParams({
      pickup: pickupLoc,
      dropoff: dropoffLoc,
      pickup_dt: form.pickup_datetime,
      return_dt: form.return_datetime,
    });
    if (form.segment !== "all") params.set("segment", form.segment);
    navigate(`/araclar?${params.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="reservation-search-form"
      className={variant === "hero" ? "bg-white p-6 sm:p-7 rounded-2xl shadow-xl border border-neutral-200/80" : "bg-white p-6 rounded-2xl shadow-md border border-neutral-200"}
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="h-9 w-9 rounded-lg bg-[#FFF0F2] flex items-center justify-center"><Search className="h-4 w-4 text-[#A11016]" /></div>
        <h3 className="text-lg font-bold text-[#171717]">Uygun Aracını Bul</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-sm text-neutral-700 mb-1.5 block">Aracı alış konumu</Label>
          <Select value={form.pickup} onValueChange={(v) => set("pickup", v)}>
            <SelectTrigger data-testid="search-pickup-location" className="h-11"><MapPin className="h-4 w-4 mr-1 text-[#A11016]" /><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {form.pickup === "Diğer" && (
            <Input className="mt-2 h-11" placeholder="Alış konumunu yazın" value={form.pickupOther} onChange={(e) => set("pickupOther", e.target.value)} data-testid="search-pickup-other" />
          )}
          {errors.pickupOther && <p className="text-xs text-[#A11016] mt-1">{errors.pickupOther}</p>}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox id="diffDropoff" checked={form.differentDropoff} onCheckedChange={(v) => set("differentDropoff", !!v)} data-testid="search-different-dropoff" />
          <Label htmlFor="diffDropoff" className="text-sm text-neutral-700 cursor-pointer">Farklı yerde teslim etmek istiyorum</Label>
        </div>

        {form.differentDropoff && (
          <div>
            <Label className="text-sm text-neutral-700 mb-1.5 block">Teslim konumu</Label>
            <Select value={form.dropoff} onValueChange={(v) => set("dropoff", v)}>
              <SelectTrigger data-testid="search-dropoff-location" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {form.dropoff === "Diğer" && (
              <Input className="mt-2 h-11" placeholder="Teslim konumunu yazın" value={form.dropoffOther} onChange={(e) => set("dropoffOther", e.target.value)} data-testid="search-dropoff-other" />
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-neutral-700 mb-1.5 block">Alış tarihi ve saati</Label>
            <Input type="datetime-local" min={minDate} value={form.pickup_datetime} onChange={(e) => set("pickup_datetime", e.target.value)} className="h-11" data-testid="search-pickup-datetime" />
            {errors.pickup_datetime && <p className="text-xs text-[#A11016] mt-1">{errors.pickup_datetime}</p>}
          </div>
          <div>
            <Label className="text-sm text-neutral-700 mb-1.5 block">İade tarihi ve saati</Label>
            <Input type="datetime-local" min={form.pickup_datetime || minDate} value={form.return_datetime} onChange={(e) => set("return_datetime", e.target.value)} className="h-11" data-testid="search-return-datetime" />
            {errors.return_datetime && <p className="text-xs text-[#A11016] mt-1">{errors.return_datetime}</p>}
          </div>
        </div>

        <div>
          <Label className="text-sm text-neutral-700 mb-1.5 block">Araç sınıfı</Label>
          <Select value={form.segment} onValueChange={(v) => set("segment", v)}>
            <SelectTrigger data-testid="search-segment" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Sınıflar</SelectItem>
              {SEGMENTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Button type="submit" data-testid="search-submit-button" className="w-full h-12 bg-[#A11016] hover:bg-[#820D12] text-white font-semibold text-base gap-2">
          <Search className="h-5 w-5" /> Uygun Araçları Bul
        </Button>
      </div>
    </form>
  );
}
